import { TFunction } from 'react-i18next';
import type { CustomColumn, TimesheetTableRow } from './TimesheetsTable.types';
import { ColumnCheckboxFilter } from './ui/ColumnCheckboxFilter';
import { ColumnSliderFilter } from './ui/ColumnSliderFilter';
import { EntityNameCell } from './ui/EntityNameCell';
import { TimesheetActualHoursCell } from './ui/TimesheetActualHoursCell';
import { TimesheetEntityStatusCell } from './ui/TimesheetEntityStatusCell';

const costPercentageSliderMarks = {
  0: '0',
  100: '100',
};

const hoursRangeSliderMarks = {
  0: '0',
  200: '200',
};

const BILLABLE_FILTERS = [
  { label: 'timesheetTable.filters.billable.label', value: 'true' },
  { label: 'timesheetTable.filters.nonBillable.label', value: 'false' },
];

const STATUS_FILTERS = [
  { label: 'timesheetTable.filters.notApproved.label', value: 'false' },
  { label: 'timesheetTable.filters.approved.label', value: 'true' },
];

export const getColumns = (
  t: TFunction<'Timesheets', string>,
  pinnedColumns: Record<string, boolean>,
): CustomColumn<TimesheetTableRow>[] => [
  {
    title: t('timesheetTable.columns.employee.title'),
    dataIndex: 'employeeName',
    key: 'employeeName',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 210,
    fixed: pinnedColumns.employeeName ? 'left' : false,
    align: 'left',
    render: (_, record: TimesheetTableRow) => <EntityNameCell record={record} />,
  },
  {
    title: t('timesheetTable.columns.status.title'),
    dataIndex: 'status',
    key: 'status',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 160,
    fixed: pinnedColumns.status ? 'left' : false,
    render: (_, record: TimesheetTableRow) => <TimesheetEntityStatusCell record={record} />,
    filterDropdown: ({ setSelectedKeys, confirm, clearFilters, selectedKeys }) => (
      <ColumnCheckboxFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearFilters}
        confirm={confirm}
        selectedKeys={selectedKeys}
        options={STATUS_FILTERS}
      />
    ),
  },
  {
    title: t('timesheetTable.columns.projectedCostHours.title'),
    dataIndex: 'projectedCostHours',
    key: 'projectedCostHours',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 160,
    fixed: pinnedColumns.projectedCostHours ? 'left' : false,
    align: 'right',
    filterDropdown: ({ setSelectedKeys, confirm, clearFilters, selectedKeys }) => (
      <ColumnSliderFilter
        marks={hoursRangeSliderMarks}
        setSelectedKeys={setSelectedKeys}
        confirm={confirm}
        clearFilters={clearFilters}
        selectedKeys={selectedKeys}
      />
    ),
  },
  {
    title: t('timesheetTable.columns.actualCostHours.title'),
    dataIndex: 'actualCostHours',
    key: 'actualCostHours',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 150,
    fixed: pinnedColumns.actualCostHours ? 'left' : false,
    align: 'right',
    filterDropdown: ({ setSelectedKeys, confirm, clearFilters, selectedKeys }) => (
      <ColumnSliderFilter
        marks={hoursRangeSliderMarks}
        setSelectedKeys={setSelectedKeys}
        confirm={confirm}
        clearFilters={clearFilters}
        selectedKeys={selectedKeys}
      />
    ),
    render: (_, record) => {
      if ('employeeName' in record) {
        const shouldBeHighlighted = record.actualCostHours !== record.projectedCostHours;

        return (
          <TimesheetActualHoursCell
            recordKey={record.key}
            columnName="actualCostHours"
            unChangedColumnName="actualBillHours"
            columnValue={record.actualCostHours}
            unChangedColumnValue={record.actualBillHours}
            highlight={shouldBeHighlighted}
            status={record.status}
            version={record.version}
          />
        );
      }

      return null;
    },
  },
  {
    title: t('timesheetTable.columns.projectedBillHours.title'),
    dataIndex: 'projectedBillHours',
    key: 'projectedBillHours',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 160,
    fixed: pinnedColumns.projectedBillHours ? 'left' : false,
    align: 'right',
    filterDropdown: ({ setSelectedKeys, confirm, clearFilters, selectedKeys }) => (
      <ColumnSliderFilter
        marks={hoursRangeSliderMarks}
        setSelectedKeys={setSelectedKeys}
        confirm={confirm}
        clearFilters={clearFilters}
        selectedKeys={selectedKeys}
      />
    ),
  },
  {
    title: t('timesheetTable.columns.actualBillHours.title'),
    dataIndex: 'actualBillHours',
    key: 'actualBillHours',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 140,
    fixed: pinnedColumns.actualBillHours ? 'left' : false,
    align: 'right',
    filterDropdown: ({ setSelectedKeys, confirm, clearFilters, selectedKeys }) => (
      <ColumnSliderFilter
        marks={hoursRangeSliderMarks}
        setSelectedKeys={setSelectedKeys}
        confirm={confirm}
        clearFilters={clearFilters}
        selectedKeys={selectedKeys}
      />
    ),
    render: (_, record) => {
      if ('employeeName' in record) {
        const shouldBeHighlighted = record.actualBillHours !== record.projectedBillHours;

        return (
          <TimesheetActualHoursCell
            recordKey={record.key}
            columnName="actualBillHours"
            unChangedColumnName="actualCostHours"
            columnValue={record.actualBillHours}
            unChangedColumnValue={record.actualCostHours}
            highlight={shouldBeHighlighted}
            status={record.status}
            version={record.version}
          />
        );
      }
      return null;
    },
  },
  {
    title: t('timesheetTable.columns.assignmentDates.title'),
    dataIndex: 'assignmentDates',
    key: 'assignmentDates',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 210,
    fixed: pinnedColumns.assignmentDates ? 'left' : false,
    align: 'left',
  },
  {
    title: t('timesheetTable.columns.invoiceStatus.title'),
    dataIndex: 'billable',
    key: 'billable',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 130,
    fixed: pinnedColumns.billable ? 'left' : false,
    align: 'left',
    filterDropdown: ({ setSelectedKeys, confirm, clearFilters, selectedKeys }) => (
      <ColumnCheckboxFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearFilters}
        confirm={confirm}
        selectedKeys={selectedKeys}
        options={BILLABLE_FILTERS}
      />
    ),
    render: (_, record: TimesheetTableRow) => {
      if ('employeeName' in record) {
        return record.billable
          ? t('timesheetTable.filters.billable.label')
          : t('timesheetTable.filters.nonBillable.label');
      }
      return null;
    },
  },
  {
    title: t('timesheetTable.columns.costPercentage.title'),
    dataIndex: 'involvement',
    key: 'involvement',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 90,
    fixed: pinnedColumns.involvement ? 'left' : false,
    align: 'right',
    filterDropdown: ({ setSelectedKeys, confirm, clearFilters, selectedKeys }) => (
      <ColumnSliderFilter
        marks={costPercentageSliderMarks}
        setSelectedKeys={setSelectedKeys}
        confirm={confirm}
        clearFilters={clearFilters}
        selectedKeys={selectedKeys}
      />
    ),
  },
  {
    title: t('timesheetTable.columns.billedPercentage.title'),
    dataIndex: 'billedInvolvement',
    key: 'billedInvolvement',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 90,
    fixed: pinnedColumns.billedInvolvement ? 'left' : false,
    align: 'right',
    filterDropdown: ({ setSelectedKeys, confirm, clearFilters, selectedKeys }) => (
      <ColumnSliderFilter
        marks={costPercentageSliderMarks}
        setSelectedKeys={setSelectedKeys}
        confirm={confirm}
        clearFilters={clearFilters}
        selectedKeys={selectedKeys}
      />
    ),
  },
  {
    title: t('timesheetTable.columns.position.title'),
    dataIndex: 'positionName',
    key: 'positionName',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 115,
    fixed: pinnedColumns.positionName ? 'left' : false,
    align: 'left',
  },
  {
    title: t('timesheetTable.columns.title.title'),
    dataIndex: 'jobTitle',
    key: 'jobTitle',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 115,
    fixed: pinnedColumns.jobTitle ? 'left' : false,
    align: 'left',
  },
  {
    title: t('timesheetTable.columns.country.title'),
    dataIndex: 'countryCode',
    key: 'countryCode',
    ellipsis: {
      showTitle: false,
    },
    sorter: true,
    width: 120,
    fixed: pinnedColumns.countryCode ? 'left' : false,
    align: 'left',
  },
  {
    title: t('timesheetTable.columns.assignmentId.title'),
    dataIndex: 'assignmentId',
    key: 'assignmentId',
    ellipsis: {
      showTitle: false,
    },
    width: 120,
    fixed: pinnedColumns.assignmentId ? 'left' : false,
    align: 'left',
  },
];

export const UNHIDDABLE_COLUMNS = ['employeeName', 'status'];
export const UNPINNABLE_COLUMNS = ['employeeName', 'status'];

export const DEFAULT_PINNABLE_COLUMNS = {
  employeeName: true,
  status: true,
  projectedCostHours: false,
  actualCostHours: false,
  projectedBillHours: false,
  actualBillHours: false,
  assignmentDates: false,
  billable: false,
  involvement: false,
  billedInvolvement: false,
  positionName: false,
  jobTitle: false,
  countryCode: false,
  assignmentId: false,
};
